import React, { useState, useEffect, useRef } from 'react';
import axios from '../../api/axios';
import './PipelineStageModal.css';

const COMPULSORY_CODES = new Set(['NEW', 'CONTACTED', 'QUALIFIED', 'WON', 'LOST']);

export const calculateDynamicStageScore = (stageName, allStages = []) => {
  if (!stageName) return { score: 0, label: '0 pts' };
  const sName = String(stageName).trim().toLowerCase();

  if (sName === 'won') {
    return { score: 100, label: '+100 pts', isWon: true };
  }
  if (sName === 'lost') {
    return { score: 0, label: '0 pts', isLost: true };
  }

  const progressionStages =
    Array.isArray(allStages) && allStages.length > 0
      ? allStages.filter((s) => {
          const name = String(typeof s === 'string' ? s : s.name || '')
            .trim()
            .toLowerCase();
          return name !== 'won' && name !== 'lost';
        })
      : ['New', 'Contacted', 'Qualified', 'Proposal', 'Negotiation'];

  const N = progressionStages.length || 1;
  const stageIndex = progressionStages.findIndex((s) => {
    const name = String(typeof s === 'string' ? s : s.name || '')
      .trim()
      .toLowerCase();
    return name === sName;
  });

  let score = 5;
  if (stageIndex !== -1) {
    const i = stageIndex + 1; // 1-indexed
    score = Math.max(1, Math.min(30, Math.round(30 * Math.pow(i / N, 1.35))));
  } else {
    const defaultProg = ['new', 'contacted', 'qualified', 'proposal', 'negotiation'];
    const defIdx = defaultProg.indexOf(sName);
    if (defIdx !== -1) {
      const idx = defIdx + 1;
      score = Math.max(1, Math.min(30, Math.round(30 * Math.pow(idx / defaultProg.length, 1.35))));
    } else {
      score = 5;
    }
  }
  return { score, label: `+${score} pts` };
};

export default function PipelineStageModal({
  isOpen,
  onClose,
  onStagesUpdated,
  currentStages = [],
  cards = [],
  isOwner = false,
}) {
  const [stages, setStages] = useState([]);
  const [newStageName, setNewStageName] = useState('');
  const [editingStageId, setEditingStageId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const getStageLeadCount = (stageName) => {
    if (!stageName || !Array.isArray(cards)) return 0;
    return cards.filter(
      (c) =>
        c &&
        !c.isArchived &&
        String(c.stage || '')
          .trim()
          .toLowerCase() === String(stageName).trim().toLowerCase()
    ).length;
  };

  // Deletion state
  const [deletingStage, setDeletingStage] = useState(null);
  const [deleteActionType, setDeleteActionType] = useState('MOVE_TO_STAGE'); // 'MOVE_TO_STAGE' | 'MOVE_TO_LOST'
  const [fallbackStageName, setFallbackStageName] = useState('Qualified');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Drag & drop reordering state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setError('');
      setSuccessMsg('');
      setNewStageName('');
      setEditingStageId(null);
      setDeletingStage(null);
      setDeleteActionType('MOVE_TO_STAGE');
      setIsDropdownOpen(false);

      if (currentStages && currentStages.length > 0) {
        setStages([...currentStages]);
      }

      // Fetch fresh stage records from backend to ensure real UUIDs
      axios
        .get('/lead-pipeline/stages')
        .then((res) => {
          if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
            setStages(res.data.data);
          }
        })
        .catch(() => {});
    }
  }, [isOpen, currentStages]);

  if (!isOpen) return null;

  const handleAddStage = async (e) => {
    e.preventDefault();
    const name = newStageName.trim();
    if (!name) {
      setError('Stage name cannot be empty');
      return;
    }
    if (stages.some((s) => s.name.toLowerCase() === name.toLowerCase())) {
      setError(`Stage "${name}" already exists in the pipeline`);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await axios.post('/lead-pipeline/stages', { name });
      if (res.data?.success) {
        setStages(res.data.stages || [...stages, res.data.data]);
        setNewStageName('');
        setSuccessMsg(`Stage "${name}" added successfully`);
        if (onStagesUpdated) onStagesUpdated(res.data.stages);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to add stage');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (stage) => {
    if (isCompulsory(stage)) return;
    setEditingStageId(stage.id);
    setEditingName(stage.name);
    setError('');
  };

  const handleSaveEdit = async (stageId) => {
    const name = editingName.trim();
    if (!name) {
      setError('Stage name cannot be empty');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await axios.put(`/lead-pipeline/stages/${stageId}`, { name });
      if (res.data?.success) {
        setStages(res.data.stages);
        setEditingStageId(null);
        setEditingName('');
        setSuccessMsg('Stage updated successfully');
        if (onStagesUpdated) onStagesUpdated(res.data.stages);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update stage');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    if (isCompulsory(stages[index]) || loading || !isOwner) {
      e.preventDefault();
      return;
    }
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    if (isCompulsory(stages[index])) return;
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    setDragOverIndex(null);
    if (draggedIndex === null || draggedIndex === targetIndex) return;
    if (isCompulsory(stages[draggedIndex]) || isCompulsory(stages[targetIndex])) {
      setDraggedIndex(null);
      return;
    }

    const fromIdx = draggedIndex;
    setDraggedIndex(null);

    const updated = [...stages];
    const [movedItem] = updated.splice(fromIdx, 1);
    updated.splice(targetIndex, 0, movedItem);

    setStages(updated);
    setLoading(true);
    setError('');
    try {
      const stageIds = updated.map((s) => s.id);
      const res = await axios.put('/lead-pipeline/stages/reorder', { stageIds });
      if (res.data?.success) {
        setStages(res.data.data);
        if (onStagesUpdated) onStagesUpdated(res.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to reorder stages');
      // revert
      setStages(currentStages);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingStage) return;
    setLoading(true);
    setError('');
    const targetFallback = deleteActionType === 'MOVE_TO_LOST' ? 'Lost' : fallbackStageName;
    try {
      const res = await axios.delete(`/lead-pipeline/stages/${deletingStage.id}`, {
        params: { fallbackStage: targetFallback },
      });
      if (res.data?.success) {
        const leadCount = getStageLeadCount(deletingStage.name);
        setStages(res.data.stages);
        setSuccessMsg(
          leadCount > 0
            ? `Stage "${deletingStage.name}" deleted. Any existing leads were reassigned to "${targetFallback}".`
            : `Stage "${deletingStage.name}" deleted successfully.`
        );
        setDeletingStage(null);
        if (onStagesUpdated) onStagesUpdated(res.data.stages);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete stage');
    } finally {
      setLoading(false);
    }
  };

  const isCompulsory = (stage) => {
    return (
      stage.is_compulsory || COMPULSORY_CODES.has((stage.code_key || stage.name).toUpperCase())
    );
  };

  return (
    <div className="crm-stage-modal-backdrop" onClick={onClose}>
      <div className="crm-stage-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crm-stage-modal-header">
          <div className="crm-stage-modal-title-wrap">
            <i className="fas fa-sliders-h crm-stage-modal-icon" />
            <div>
              <h3>Pipeline Stage Management</h3>
              <p>
                Configure stages for your workspace. Compulsory stages are protected to maintain
                core CRM flows.
              </p>
            </div>
          </div>
          <button type="button" className="crm-stage-modal-close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="crm-stage-modal-body">
          {error && (
            <div className="crm-stage-alert crm-stage-alert-error">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}
          {successMsg && (
            <div className="crm-stage-alert crm-stage-alert-success">
              <i className="fas fa-check-circle" /> {successMsg}
            </div>
          )}

          {/* Add custom stage form at the top */}
          {isOwner && (
            <form onSubmit={handleAddStage} className="crm-stage-add-form">
              <div className="crm-stage-add-header">
                <i className="fas fa-plus-circle crm-stage-add-icon" />
                <span className="crm-stage-add-title">Add Custom Stage</span>
              </div>
              <div className="crm-stage-add-row">
                <input
                  type="text"
                  placeholder="e.g. Product Demo, Negotiation, Review"
                  value={newStageName}
                  onChange={(e) => setNewStageName(e.target.value)}
                  className="crm-stage-input"
                  maxLength={100}
                  disabled={loading}
                />
                <button
                  type="submit"
                  className="crm-stage-btn crm-stage-btn-primary"
                  disabled={loading || !newStageName.trim()}
                >
                  <i className="fas fa-plus" /> Add Stage
                </button>
              </div>
            </form>
          )}

          {/* Stage list */}
          <div className="crm-stage-list">
            {stages.map((stage, idx) => {
              const compulsory = isCompulsory(stage);
              const isEditing = editingStageId === stage.id;
              const isDeletingThis = deletingStage && deletingStage.id === stage.id;
              const leadCount = getStageLeadCount(stage.name);

              if (isDeletingThis) {
                // If stage has 0 leads: render simple confirmation in place of this stage row
                if (leadCount === 0) {
                  return (
                    <div key={stage.id || stage.name} className="crm-stage-delete-confirm-box">
                      <div className="crm-stage-delete-header">
                        <i className="fas fa-trash-alt crm-stage-delete-icon" />
                        <div>
                          <h4>Delete Stage: "{stage.name}"?</h4>
                          <p>
                            Are you sure you want to delete this stage? There are currently no leads
                            in this stage.
                          </p>
                        </div>
                      </div>

                      <div className="crm-stage-confirm-actions">
                        <button
                          type="button"
                          className="crm-stage-btn crm-stage-btn-cancel"
                          onClick={() => setDeletingStage(null)}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="crm-stage-btn crm-stage-btn-danger"
                          onClick={handleConfirmDelete}
                          disabled={loading}
                        >
                          {loading ? 'Deleting...' : 'Delete Stage'}
                        </button>
                      </div>
                    </div>
                  );
                }

                // If stage has leads (> 0): render options dialog in place of this stage row
                return (
                  <div key={stage.id || stage.name} className="crm-stage-delete-confirm-box">
                    <div className="crm-stage-delete-header">
                      <i className="fas fa-exclamation-triangle crm-stage-delete-icon" />
                      <div>
                        <h4>Delete Stage: "{stage.name}"</h4>
                        <p>
                          This stage currently has {leadCount} active lead{leadCount > 1 ? 's' : ''}
                          . What would you like to do with them?
                        </p>
                      </div>
                    </div>

                    <div className="crm-stage-delete-options">
                      {/* Option 1: Move to Lost */}
                      <div
                        className={`crm-stage-option-card ${
                          deleteActionType === 'MOVE_TO_LOST' ? 'is-selected is-lost' : ''
                        }`}
                        onClick={() => setDeleteActionType('MOVE_TO_LOST')}
                      >
                        <input
                          type="radio"
                          name="deleteAction"
                          value="MOVE_TO_LOST"
                          checked={deleteActionType === 'MOVE_TO_LOST'}
                          onChange={() => setDeleteActionType('MOVE_TO_LOST')}
                        />
                        <div className="crm-stage-option-info">
                          <span className="crm-stage-option-title">
                            <i className="fas fa-times-circle" style={{ color: '#ef4444' }} />{' '}
                            Option 1: Move to "Lost"
                          </span>
                          <span className="crm-stage-option-desc">
                            Remove leads from the active pipeline and mark them as Closed / Lost.
                          </span>
                        </div>
                      </div>

                      {/* Option 2: Move to Another Stage */}
                      <div
                        className={`crm-stage-option-card ${
                          deleteActionType === 'MOVE_TO_STAGE' ? 'is-selected is-stage' : ''
                        }`}
                        onClick={() => setDeleteActionType('MOVE_TO_STAGE')}
                      >
                        <input
                          type="radio"
                          name="deleteAction"
                          value="MOVE_TO_STAGE"
                          checked={deleteActionType === 'MOVE_TO_STAGE'}
                          onChange={() => setDeleteActionType('MOVE_TO_STAGE')}
                        />
                        <div className="crm-stage-option-info">
                          <span className="crm-stage-option-title">
                            <i className="fas fa-exchange-alt" style={{ color: '#3b82f6' }} />{' '}
                            Option 2: Move to Another Stage
                          </span>
                          <span className="crm-stage-option-desc">
                            Reassign leads to a different stage in your active sales pipeline.
                          </span>
                          {deleteActionType === 'MOVE_TO_STAGE' && (
                            <div
                              className="crm-stage-custom-select-wrap"
                              ref={dropdownRef}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className={`crm-stage-custom-select-trigger ${
                                  isDropdownOpen ? 'is-open' : ''
                                }`}
                                onClick={() => setIsDropdownOpen((prev) => !prev)}
                              >
                                <div className="crm-stage-select-current-val">
                                  <span className="crm-stage-select-name">
                                    {fallbackStageName || 'Select Stage'}
                                  </span>
                                </div>
                                <i
                                  className={`fas fa-chevron-down crm-stage-select-arrow ${
                                    isDropdownOpen ? 'rotate-180' : ''
                                  }`}
                                />
                              </button>

                              {isDropdownOpen && (
                                <div className="crm-stage-custom-select-menu">
                                  {stages
                                    .filter(
                                      (s) => s.id !== stage.id && s.name.toLowerCase() !== 'lost'
                                    )
                                    .map((s) => {
                                      const selected = s.name === fallbackStageName;
                                      return (
                                        <div
                                          key={s.id}
                                          className={`crm-stage-custom-select-item ${
                                            selected ? 'is-selected' : ''
                                          }`}
                                          onClick={() => {
                                            setFallbackStageName(s.name);
                                            setIsDropdownOpen(false);
                                          }}
                                        >
                                          <span className="crm-stage-select-item-name">
                                            {s.name}
                                          </span>
                                          {selected && (
                                            <i className="fas fa-check crm-stage-select-check" />
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="crm-stage-confirm-actions">
                      <button
                        type="button"
                        className="crm-stage-btn crm-stage-btn-cancel"
                        onClick={() => setDeletingStage(null)}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="crm-stage-btn crm-stage-btn-danger"
                        onClick={handleConfirmDelete}
                        disabled={loading}
                      >
                        {loading ? 'Deleting...' : 'Confirm & Delete'}
                      </button>
                    </div>
                  </div>
                );
              }

              const isDraggable = isOwner && !compulsory && !loading && !isEditing;
              const scoreInfo = calculateDynamicStageScore(stage.name, stages);

              return (
                <div
                  key={stage.id || stage.name}
                  className={`crm-stage-item ${compulsory ? 'is-compulsory' : 'is-custom-stage'} ${
                    draggedIndex === idx ? 'is-dragging' : ''
                  } ${dragOverIndex === idx ? 'is-drag-over' : ''}`}
                  draggable={isDraggable}
                  onDragStart={(e) => handleDragStart(e, idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  onDrop={(e) => handleDrop(e, idx)}
                >
                  <div className="crm-stage-item-order">
                    <span className="crm-stage-order-num">{idx + 1}</span>
                    {isOwner && !compulsory ? (
                      <div className="crm-stage-drag-handle" title="Drag to reorder stage">
                        <i className="fas fa-grip-vertical" />
                      </div>
                    ) : null}
                  </div>

                  <div className="crm-stage-item-content">
                    {isEditing ? (
                      <div className="crm-stage-inline-edit">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="crm-stage-input"
                          maxLength={100}
                          autoFocus
                        />
                        <button
                          type="button"
                          className="crm-stage-btn crm-stage-btn-primary crm-stage-btn-sm"
                          onClick={() => handleSaveEdit(stage.id)}
                          disabled={loading}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="crm-stage-btn crm-stage-btn-cancel crm-stage-btn-sm"
                          onClick={() => setEditingStageId(null)}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="crm-stage-name-wrap">
                        <span className="crm-stage-name">{stage.name}</span>
                        <span
                          className={`crm-stage-score-badge ${
                            scoreInfo.isWon ? 'is-won' : scoreInfo.isLost ? 'is-lost' : ''
                          }`}
                          title={`Lead score contribution: ${scoreInfo.label}`}
                        >
                          {scoreInfo.label}
                        </span>
                      </div>
                    )}
                  </div>

                  {isOwner && !isEditing && (
                    <div className="crm-stage-item-actions">
                      {!compulsory ? (
                        <>
                          <button
                            type="button"
                            className="crm-stage-action-btn"
                            onClick={() => handleStartEdit(stage)}
                            title="Rename stage"
                            disabled={loading}
                          >
                            <i className="fas fa-pencil-alt" />
                          </button>
                          <button
                            type="button"
                            className="crm-stage-action-btn crm-stage-action-delete"
                            onClick={() => {
                              setDeletingStage(stage);
                              setDeleteActionType('MOVE_TO_STAGE');
                              const other = stages.filter(
                                (s) => s.id !== stage.id && s.name.toLowerCase() !== 'lost'
                              );
                              const defaultFallback =
                                other.find((s) => s.name.toLowerCase() === 'qualified')?.name ||
                                other[0]?.name ||
                                'Qualified';
                              setFallbackStageName(defaultFallback);
                            }}
                            title="Delete custom stage"
                            disabled={loading}
                          >
                            <i className="fas fa-trash-alt" />
                          </button>
                        </>
                      ) : (
                        <div
                          className="crm-stage-lock-badge"
                          title="Default core stage (cannot be modified or deleted)"
                        >
                          <i className="fas fa-lock" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="crm-stage-modal-footer">
          <button type="button" className="crm-stage-btn crm-stage-btn-cancel" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
